import { getRepeaterValue, RepeaterCoordinate, setRepeaterValue } from "../utils/repeaterPath";
import AddCollection from "./common/AddCollection/AddCollection";
import AddContent from "./common/AddContent/AddContent";
import { AutoCompleteDropDown } from "./common/AutoCompleteDropDown/AutoCompleteDropDown";
import ButtonField from "./common/Button/Button";
import CheckBoxComponent from "./common/CheckBox/CheckBoxComponent";
import Code from "./common/Code/Code";
import ColorPicker from "./common/ColorPicker/ColorPicker";
import ComponentRender from "./common/ComponentRender/ComponentRender";
import DateAndTimePicker from "./common/DateAndTimePicker/DateAndTimePicker";
import DatePickerComponent from "./common/DatePicker/DatePicker";
import { Dropdown } from "./common/DropDown/Dropdown";
import Editor from "./common/Editor/Editor";
import GallerywithOptions from "./common/GallerywithOptions/GallerywithOptions";
import IconPicker from "./common/IconPicker/IconPicker";
import ImagePicker from "./common/ImagePicker/ImagePicker";
import ImagePickerWithoutCrop from "./common/ImagePickerWithoutCrop/ImagePickerWithoutCrop";
import ImageOptions from "./common/ImageWithOptions/ImageOptions";
import ImageWithTextBox from "./common/ImageWithTextBox/ImageWithTextBox";
import MediaHandle from "./common/MediaHandle/MediaHandle";
import PagePicker from "./common/PageListing/PagePicker";
import Query from "./common/Query/Query";
import QueryOrMultiPicker from "./common/QueryOrMultiPicker/QueryOrMultiPicker";
import RadioGroupButtons from "./common/RadioGroup/RadioGroupButtons";
import CustomRepeater from "./common/Repeater/CustomRepeater";
import TagSelectorField from "./common/TagListing/TagSelectorField";
import TextArea from "./common/TextArea/TextArea";
import TextBox from "./common/TextBox/TextBox";
import Toggle from "./common/Toggle/Toggle";
import VideoOptions from "./common/VideoWithOptions/VideoOptions";
import Custom1 from "./customComponents/Custom1/Custom1";
import ExcelFileUpload from "./customComponents/ExcelFileUpload/ExcelFileUpload";
import RadioCustomOptions from "./customComponents/RadioCustomOptions/RadioCustomOptions";

// Field descriptor as seen by the builder. For fields rendered inside a repeater the
// CustomRepeater also attaches the repeater coordinates (repeaterPath/childName) so we
// never have to reverse-parse them out of the (underscore-containing) `name`. `repeaterPath`
// carries the full chain of ancestor repeater array/index pairs, so a repeater nested inside
// another repeater resolves correctly at any depth, not just one level in.
interface FieldMeta {
  name?: string;
  type?: string;
  repeaterPath?: RepeaterCoordinate[];
  childName?: string;
  [key: string]: any;
}

const handleChange = (
  event,
  type,
  clearErrors,
  setState,
  state,
  isRepeater = false,
  field: FieldMeta | null = null,
) => {
  clearErrors(event.target.name);
  if (isRepeater) {
    //For a field rendered inside a (possibly nested) repeater — use the coordinates
    //carried on the field object instead of reverse-parsing the name (names may
    //contain underscores).
    setState(
      setRepeaterValue(state, field?.repeaterPath, field?.childName ?? "", event.target.value),
    );
  } else if (type === "number") {
    setState({
      ...state,
      [event.target.name]: event.target.value.replace(/[^0-9]/g, ""),
    });
  } else {
    setState({
      ...state,
      [event.target.name]: event.target.value,
    });
  }
};
const handleToggleChange = (
  event,
  setState,
  state,
  isRepeater = false,
  field: FieldMeta | null = null,
) => {
  //For Boolean type components
  if (isRepeater) {
    //For a field rendered inside a (possibly nested) repeater — use the coordinates
    //carried on the field object instead of reverse-parsing the name (names may
    //contain underscores).
    const fieldName = field?.childName ?? "";
    const currentValue = getRepeaterValue(state, field?.repeaterPath, fieldName);
    setState(setRepeaterValue(state, field?.repeaterPath, fieldName, !currentValue));
    return;
  }
  setState({
    ...state,
    [event.target.name]: !state[event.target.name],
  });
};
const handleOnBlur = (
  event,
  type,
  setState,
  state,
  isRepeater = false,
  field: FieldMeta | null = null,
) => {
  if (isRepeater) {
    //For a field rendered inside a (possibly nested) repeater — use the coordinates
    //carried on the field object instead of reverse-parsing the name (names may
    //contain underscores).
    setState(
      setRepeaterValue(state, field?.repeaterPath, field?.childName ?? "", event.target.value),
    );
  } else if (type === "number") {
    setState({
      ...state,
      [event.target.name]: event.target.value.replace(/[^0-9]/g, ""),
    });
  } else {
    setState({
      ...state,
      [event.target.name]: event.target.value,
    });
  }
};
// For a field rendered inside a repeater, `field.name` is the RHF-path string
// (e.g. `past_champion_entries[0].champion_image_past_champion_entries_93709`)
// — not a real state key. Writing it straight into `state` (as this used to do)
// leaks a stray top-level key instead of updating the array item, and the
// backend then rejects the payload as an unknown field. Use the repeater
// coordinates carried on the field object instead, mirroring handleChange's
// repeater branch above.
const setImageFieldValue =
  (imageValue, isRepeater, field: FieldMeta | null) => (clearErrors, setState, state) => {
    if (isRepeater) {
      const fieldName = field?.childName ?? "";
      clearErrors(fieldName);
      setState(setRepeaterValue(state, field?.repeaterPath, fieldName, imageValue));
      return;
    }
    const name = field?.name ?? "";
    clearErrors(name);
    setState({
      ...state,
      [name]: {
        ...state[name],
        ...imageValue,
      },
    });
  };
const updateField =
  (updatedPartialObj, isRepeater = false, field: FieldMeta | null = null) =>
  (clearErrors, setState, state) =>
    setImageFieldValue(
      {
        original_image: updatedPartialObj?.original_image,
        published_images: updatedPartialObj?.published_images,
      },
      isRepeater,
      field,
    )(clearErrors, setState, state);
const handleChangeOn =
  (newValue, field, multiple, isRepeater = false) =>
  (clearErrors, setState, state, fields) => {
    clearErrors(field?.name);
    const value = multiple ? newValue : [newValue];
    const dependentField = fields?.find(
      (item) => item.dependent_name === (isRepeater ? field?.childName : field?.name),
    );
    if (isRepeater) {
      let nextState = setRepeaterValue(state, field?.repeaterPath, field?.childName ?? "", value);
      if (dependentField) {
        nextState = setRepeaterValue(nextState, field?.repeaterPath, dependentField?.name, []);
      }
      setState(nextState);
      return;
    }
    if (dependentField) {
      setState({
        ...state,
        [field.name]: value,
        [dependentField?.name]: [],
      });
    } else {
      setState({
        ...state,
        [field.name]: value,
      });
    }
  };
const updateState =
  (name, value, isRepeater = false, field: FieldMeta | null = null) =>
  (clearErrors, setState, state) => {
    //For TagListing Component
    clearErrors(name);
    if (isRepeater) {
      //For a field rendered inside a (possibly nested) repeater — use the coordinates
      //carried on the field object instead of reverse-parsing the name (names may
      //contain underscores), mirroring handleChange's repeater branch above.
      setState(setRepeaterValue(state, field?.repeaterPath, field?.childName ?? "", value));
      return;
    }
    setState({
      ...state,
      [name]: value,
    });
  };

// const updateAssetWithText = (updatedPartialObj, name) => (clearErrors, setState, state) => {
//   //For ImagePicker Component
//   console.log("shadowUpdated", updatedPartialObj);
//   clearErrors(name);
//   setState({
//     ...state,
//     [name]: {
//       ...state[name],
//       original_image: updatedPartialObj?.original_image,
//       published_images: updatedPartialObj?.published_images,
//     },
//   });
// };

const optionCallBack = (field, state) => {
  //For Dynamic Dropdown
  if (state?.[field?.dependent_name]) {
    return field?.options?.filter((item) =>
      state?.[field?.dependent_name]?.map((x) => x.id).includes(item.parentId),
    );
  } else {
    return field?.options;
  }
};

// `errors` is narrowed by exactly one repeater level on every CustomRepeater
// render before being handed down (see CustomRepeater.tsx), so by the time it
// reaches a leaf field here it is already scoped to that field's own item —
// no path-walking needed, just the field's own key.
const errorDestructure = (errors, field, isRepeater = false) => {
  if (isRepeater) {
    return errors?.[field?.childName];
  }
  return errors?.[field?.name];
};

const dataDestructure = (state, field, isRepeater = false) => {
  if (isRepeater) {
    //Read the value using the coordinates carried on the field object instead of
    //reverse-parsing the name (names may contain underscores, e.g. `dam_key_name`).
    return getRepeaterValue(state, field?.repeaterPath, field?.childName) || "";
  }
  return state?.[field?.name] || "";
};

function builder(
  field,
  state,
  errors,
  register,
  clearErrors,
  setState,
  fields,
  isEdit,
  title,
  control,
  callbackEvents,
  isRepeater = false,
  category = "",
) {
  // Driven by the schema (`disabled: true`); defaults to false when absent.
  const isFieldDisabled = field?.disabled ?? false;
  switch (field?.type) {
    case "text":
      switch (field.variant) {
        case "multiline":
          return (
            <TextArea
              field={field}
              isDisabled={isFieldDisabled}
              errors={errors}
              register={register}
              clearErrors={clearErrors}
              data={dataDestructure(state, field, isRepeater)}
              handleChange={(event) =>
                handleChange(event, field?.type, clearErrors, setState, state, isRepeater, field)
              }
              handleOnBlur={(event) =>
                handleOnBlur(event, field?.type, setState, state, isRepeater, field)
              }
            />
          );
        case "editor":
          return (
            <Editor
              isDisabled={isFieldDisabled}
              field={field}
              errors={errors}
              control={control}
              data={dataDestructure(state, field, isRepeater)}
              title={state["title"] || ""}
              updateState={(name, value) =>
                updateState(name, value, isRepeater, field)(clearErrors, setState, state)
              }
            />
          );
        case "local_upload": //excel file upload from local from customComponents
          return (
            <ExcelFileUpload
              isDisabled={isFieldDisabled}
              item={field}
              control={control}
              data={state[field?.name]}
              updateState={(name, value) => updateState(name, value)(clearErrors, setState, state)}
            />
          );
        case "number":
          return (
            <TextBox
              type='number'
              field={field}
              isDisabled={isFieldDisabled}
              error={errorDestructure(errors, field, isRepeater)}
              register={register}
              clearErrors={clearErrors}
              data={dataDestructure(state, field, isRepeater)}
              handleChange={(event) =>
                handleChange(event, field?.type, clearErrors, setState, state, isRepeater, field)
              }
              handleOnBlur={(event) =>
                handleOnBlur(event, field?.type, setState, state, isRepeater, field)
              }></TextBox>
          );
        case "code":
          return (
            <Code
              field={field}
              state={state}
              setState={setState}
              control={control}
              clearErrors={clearErrors}
              error={errorDestructure(errors, field, isRepeater)}
              data={dataDestructure(state, field, isRepeater)}
              isDisabled={isFieldDisabled}
              isRepeater={isRepeater}
              handleChange={(event) =>
                handleChange(event, field?.type, clearErrors, setState, state, isRepeater, field)
              }
            />
          );
        default:
          return (
            <TextBox
              type='text'
              field={field}
              isDisabled={isFieldDisabled}
              error={errorDestructure(errors, field, isRepeater)}
              register={register}
              clearErrors={clearErrors}
              data={dataDestructure(state, field, isRepeater)}
              handleChange={(event) =>
                handleChange(event, field?.type, clearErrors, setState, state, isRepeater, field)
              }
              handleOnBlur={(event) =>
                handleOnBlur(event, field?.type, setState, state, isRepeater, field)
              }></TextBox>
          );
      }
    case "color":
      return (
        <ColorPicker
          isDisabled={isFieldDisabled}
          field={field}
          error={errorDestructure(errors, field, isRepeater)}
          register={register}
          clearErrors={clearErrors}
          data={dataDestructure(state, field, isRepeater)}
          handleChange={(event) =>
            handleChange(event, field?.type, clearErrors, setState, state, isRepeater, field)
          }
          handleOnBlur={(event) =>
            handleOnBlur(event, field?.type, setState, state, isRepeater, field)
          }
        />
      );
    case "img_gallery":
      switch (field?.variant) {
        case "no_crop":
          return (
            <ImageOptions
              setState={setState}
              state={state}
              field={field}
              variant='no_crop'
              isDisabled={isFieldDisabled}
            />
          );
        default:
          return (
            <ImageOptions
              setState={setState}
              state={state}
              field={field}
              isDisabled={isFieldDisabled}

              // updateAssetWithText={(updatedPartialObj, name) =>
              //   updateAssetWithText(updatedPartialObj, name)(clearErrors, setState, state)
              // }
            />
          );
      }
    case "vid_gallery":
      return (
        <VideoOptions
          setState={setState}
          state={state}
          field={field}
          isDisabled={isFieldDisabled}
        />
      );
    case "gallery":
      return (
        <GallerywithOptions
          setState={setState}
          state={state}
          field={field}
          isDisabled={isFieldDisabled}
        />
      );
    case "image":
      switch (field?.variant) {
        case "no_crop":
          return (
            <ImagePickerWithoutCrop
              isDisabled={isFieldDisabled}
              field={field}
              error={errorDestructure(errors, field, isRepeater)}
              register={register}
              data={dataDestructure(state, field, isRepeater)}
              updateField={(updatedPartialObj) =>
                updateField(updatedPartialObj, isRepeater, field)(clearErrors, setState, state)
              }
              containerSize={{ width: "100%", height: "192px" }}
              aspect_ratio={field?.aspect_ratio}
            />
          );
        case "no_crop_variant_2":
          return (
            <ImagePickerWithoutCrop
              isDisabled={isFieldDisabled}
              field={field}
              error={errorDestructure(errors, field, isRepeater)}
              register={register}
              data={dataDestructure(state, field, isRepeater)}
              updateField={(updatedPartialObj) =>
                updateField(updatedPartialObj, isRepeater, field)(clearErrors, setState, state)
              }
              containerSize={{ width: "100%", height: "192px" }}
              aspect_ratio={field?.aspect_ratio}
            />
          );
        case "no_crop_variant_3":
          return (
            <ImagePickerWithoutCrop
              isDisabled={isFieldDisabled}
              field={field}
              error={errorDestructure(errors, field, isRepeater)}
              register={register}
              data={dataDestructure(state, field, isRepeater)}
              updateField={(updatedPartialObj) =>
                updateField(updatedPartialObj, isRepeater, field)(clearErrors, setState, state)
              }
              containerSize={{ width: "50px", height: "50px" }}
              aspect_ratio={field?.aspect_ratio}
            />
          );
        default:
          return (
            <ImagePicker
              isDisabled={isFieldDisabled}
              field={field}
              error={errorDestructure(errors, field, isRepeater)}
              register={register}
              data={dataDestructure(state, field, isRepeater)}
              updateField={(updatedPartialObj) =>
                updateField(updatedPartialObj, isRepeater, field)(clearErrors, setState, state)
              }
            />
          );
      }

    case "content":
      switch (field?.variant) {
        case "contentType":
          return (
            <AddContent
              isDisabled={isFieldDisabled}
              state={state}
              setState={setState}
              field={field}
              error={errors?.[field.name]}
              register={register}
              clearErrors={clearErrors}
              isRepeater={isRepeater}
            />
          );
        case "collection":
          return (
            <AddCollection
              isDisabled={isFieldDisabled}
              state={state}
              setState={setState}
              field={field}
              error={errors?.[field.name]}
              register={register}
              clearErrors={clearErrors}
              isRepeater={isRepeater}
            />
          );
        case "page":
          return (
            <PagePicker
              state={state}
              setState={setState}
              field={field}
              isDisabled={isFieldDisabled}
            />
          );
        default:
          return null;
      }

    case "component_render":
      return (
        <ComponentRender
          isDisabled={isFieldDisabled}
          field={field}
          state={state}
          setState={setState}
          errors={errors}
          register={register}
          clearErrors={clearErrors}
          control={control}
        />
      );

    // return (
    //   <ContentTypePicker
    //     field={field}
    //     errors={errors}
    //     register={register}
    //     state={state}
    //     updateField={() => {}}
    //   />
    // );
    case "query":
      return (
        <Query
          isDisabled={isFieldDisabled}
          field={field}
          state={state}
          errors={errors}
          setState={setState}
          register={register}
          isEdit={isEdit}
          clearErrors={clearErrors}
          isRepeater={isRepeater}
        />
      );
    case "queryorpicker":
      return (
        <QueryOrMultiPicker
          isDisabled={isFieldDisabled}
          field={field}
          state={state}
          errors={errors}
          setState={setState}
          register={register}
          isEdit={isEdit}
          clearErrors={clearErrors}
          isRepeater={isRepeater}
        />
      );
    case "date":
      return (
        <DatePickerComponent
          isDisabled={isFieldDisabled}
          disablePast={field?.disable_past}
          state={state}
          setState={setState}
          field={field}
          register={register}
          clearErrors={clearErrors}
          error={errors?.[field.name]}
          control={control}
        />
      );
    case "radio":
      return (
        <RadioGroupButtons
          value={field?.options}
          disabled={isFieldDisabled}
          field={field}
          row={true}
          handleChange={(event) =>
            handleChange(event, field?.type, clearErrors, setState, state, isRepeater, field)
          }
          error={errorDestructure(errors, field, isRepeater)}
          control={control}
          clearErrors={clearErrors}
          data={dataDestructure(state, field, isRepeater)}
        />
      );
    case "dropdown": {
      const dropdownState = isRepeater
        ? {
            [field?.name]: getRepeaterValue(state, field?.repeaterPath, field?.childName ?? ""),
            ...(field?.dependent_name
              ? {
                  [field.dependent_name]: getRepeaterValue(
                    state,
                    field?.repeaterPath,
                    field.dependent_name,
                  ),
                }
              : {}),
          }
        : state;

      return field?.variant === "searchdropdown" ? (
        <AutoCompleteDropDown
          isDisabled={isFieldDisabled}
          options={field?.dependent_name ? optionCallBack(field, dropdownState) : field?.options}
          state={dropdownState}
          field={field}
          optionFormat='label'
          error={errorDestructure(errors, field, isRepeater)}
          register={register}
          handleChangeOn={(newValue, currentField, multiple) =>
            handleChangeOn(newValue, currentField, multiple, isRepeater)(
              clearErrors,
              setState,
              state,
              fields,
            )
          }
        />
      ) : (
        <Dropdown
          isDisabled={isFieldDisabled}
          options={field?.options}
          setState={
            isRepeater
              ? (updated) =>
                  setState(
                    setRepeaterValue(
                      state,
                      field?.repeaterPath,
                      field?.childName ?? "",
                      updated?.[field?.name],
                    ),
                  )
              : setState
          }
          state={dropdownState}
          error={errorDestructure(errors, field, isRepeater)}
          register={register}
          field={field}
          clearErrors={clearErrors}
          callbackEvents={callbackEvents}
          control={control}
        />
      );
    }
    // case "checkbox":
    //   return (
    //     <CheckBoxComponent
    //       field={field}
    //       error={errors?.[field.name]}
    //       register={register}
    //       handleChange={handleToggleChange}
    //       checked={state[field?.name]}
    //     />
    //   );
    case "boolean":
      switch (field.variant) {
        case "toggle":
          return (
            <Toggle
              field={field}
              error={errorDestructure(errors, field, isRepeater)}
              disabled={isFieldDisabled}
              color={"#000000"}
              onChange={(event) => handleToggleChange(event, setState, state, isRepeater, field)}
              checked={dataDestructure(state, field, isRepeater)}
              name={field?.name}
              register={register}
            />
          );
        case "checkbox":
          return (
            <CheckBoxComponent
              disabled={isFieldDisabled}
              field={field}
              error={errorDestructure(errors, field, isRepeater)}
              register={register}
              handleChange={(event) =>
                handleToggleChange(event, setState, state, isRepeater, field)
              }
              checked={dataDestructure(state, field, isRepeater)}
            />
          );
        default:
          return null;
      }
    case "tag": {
      const hasPrimaryTagsField = (fields || []).some(
        (siblingField) => siblingField?.type === "tag" && siblingField?.name === "tags",
      );
      if (field?.name === "tag_sm" && hasPrimaryTagsField) return null; //to avoid two times render
      return (
        Object.keys(state).length > 0 && (
          <TagSelectorField
            isDisabled={isFieldDisabled}
            field={field}
            isEdit={isEdit}
            selectedTag={field?.name === "tag_sm" ? state["tags"] : state[field?.name]}
            category={category}
            subCategory={title}
            register={register}
            error={errors?.[field.name]}
            state={state}
            setState={setState}
            clearErrors={clearErrors}
          />
        )
      );
    }
    case "datetime":
      return (
        <DateAndTimePicker
          isDisabled={isFieldDisabled}
          disablePast={field?.disable_past}
          field={field}
          error={errors?.[field.name]}
          state={state}
          updateState={(name, value) => updateState(name, value)(clearErrors, setState, state)}
          clearErrors={clearErrors}
          control={control}
        />
      );
    case "custom1": //Challenge Rule // This is a sample component developed for healthcare.
      return (
        <Custom1
          type='text'
          field={field}
          isDisabled={isFieldDisabled}
          errors={errors}
          register={register}
          clearErrors={clearErrors}
          data={state[field?.name]}
          handleChange={(event) => handleChange(event, field?.type, clearErrors, setState, state)}
          handleOnBlur={(event) => handleOnBlur(event, field?.type, setState, state)}
        />
      );
    case "radioCustomOptions":
      return (
        <RadioCustomOptions
          isDisabled={isFieldDisabled}
          field={field}
          state={state}
          setState={setState}
          errors={errors}
          register={register}
          clearErrors={clearErrors}
          control={control}
          fields={fields}
          isEdit={isEdit}
          title={title}
        />
      );
    case "repeater":
      return (
        <CustomRepeater
          isDisabled={isFieldDisabled}
          field={field}
          control={control}
          errors={errors}
          register={register}
          clearErrors={clearErrors}
          state={state}
          setState={setState}
          isEdit={isEdit}
          title={title}
        />
      );
    case "imagewithtext":
      return (
        <ImageWithTextBox
          isDisabled={isFieldDisabled}
          field={field}
          errors={errors}
          register={register}
          clearErrors={clearErrors}
          state={state}
          updateField={(updatedPartialObj) =>
            updateField(updatedPartialObj, isRepeater, field)(clearErrors, setState, state)
          }
          handleChange={(event) =>
            handleChange(event, field?.type, clearErrors, setState, state, isRepeater, field)
          }
          handleOnBlur={(event) =>
            handleOnBlur(event, field?.type, setState, state, isRepeater, field)
          }
          containerSize={{ width: "50px", height: "50px" }}
        />
      );
    case "button":
      return (
        <ButtonField
          field={field}
          control={control}
          state={dataDestructure(state, field, isRepeater)}
          setState={(value) =>
            updateState(field?.name, value, isRepeater, field)(clearErrors, setState, state)
          }
          isDisabled={isFieldDisabled}
        />
      );
    case "icon":
      return (
        <IconPicker
          isDisabled={isFieldDisabled}
          field={field}
          state={state}
          setState={setState}
          errors={errors}
          register={register}
          clearErrors={clearErrors}
        />
      );
    case "mediahandle":
      return (
        <MediaHandle
          isDisabled={isFieldDisabled}
          field={field}
          state={state}
          setState={setState}
          errors={errors}
          register={register}
          clearErrors={clearErrors}
        />
      );
    default:
      return null;
  }
}
export default builder;
